import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

import { IconButton, Popover, makeStyles, useTheme } from '@material-ui/core';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import {
  MouseEvent,
  forwardRef,
  useCallback,
  useMemo,
  useState,
  useEffect,
} from 'react';
import { Calendar } from 'react-date-range';
import {
  AccessibilityProps,
  WbTextField,
  WbTextFieldProps,
} from './WbTextField';
import { ClearButton } from './ClearButton';

export type WbDatePickerProps = Omit<
  WbTextFieldProps,
  'type' | 'onChange' | 'value' | 'inputProps' | 'SelectProps' | 'InputProps'
> &
  AccessibilityProps & {
    value?: string;
    onChange: (value?: string) => void;
    id?: string;
    ariaLabel?: string;
    label?: string | React.ReactNode;
    minDate?: Date;
    maxDate?: Date;
    closeOnSelect?: boolean;
  };

const useStyles = makeStyles(
  theme => ({
    modalRoot: {
      background: theme.palette.background.default,
      fontFamily: theme.typography.fontFamily,
      '--font-family': theme.typography.fontFamily as unknown as string,
      display: 'flex',
      flexDirection: 'column',
      padding: theme.spacing(1),
    },
  }),
  { name: 'WbDatePicker' },
);

const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);

const toYyyyMmDd = (d: Date) => {
  return `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())}`;
};

const fromYyyyMmDd = (s?: string): Date | undefined => {
  if (!s) return undefined;
  const m = /^(\d{4})\/(\d{2})\/(\d{2})$/.exec(s);
  if (!m) return undefined;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  const d = new Date(year, month - 1, day, 0, 0, 0, 0);
  if (isNaN(d.getTime())) return undefined;
  return d;
};

export const WbDatePicker = forwardRef<HTMLDivElement, WbDatePickerProps>(
  (props, ref) => {
    const {
      value,
      onChange,
      label,
      id,
      ariaLabel,
      disabled,
      minDate,
      maxDate,
      placeholder,
      fullWidth,
      closeOnSelect = true,
      helperText,
      error,
      style,
      ...rest
    } = props;

    const classes = useStyles();
    const theme = useTheme();

    const [anchorEl, setAnchorEl] = useState<Element | null>(null);
    const [selectedDate, setSelectedDate] = useState<Date | undefined>(
      fromYyyyMmDd(value),
    );

    useEffect(() => {
      setSelectedDate(fromYyyyMmDd(value));
    }, [value]);

    const handleIconClick = useCallback(
      (e: MouseEvent<HTMLButtonElement>) => {
        if (disabled) return;
        setAnchorEl(e.currentTarget);
      },
      [disabled],
    );

    const handleInputClick = useCallback(
      (e: MouseEvent<HTMLInputElement>) => {
        if (disabled) return;
        setAnchorEl(e.currentTarget);
      },
      [disabled],
    );

    const open = Boolean(anchorEl);

    const handleClose = useCallback(() => setAnchorEl(null), []);

    const handleSelect = useCallback(
      (date: Date) => {
        const formatted = toYyyyMmDd(date);
        setSelectedDate(date);
        onChange?.(formatted);
        if (closeOnSelect) handleClose();
      },
      [closeOnSelect, handleClose, onChange],
    );

    const accessibilityProps = useMemo(() => {
      return id && label
        ? { id, label }
        : { id, ariaLabel: ariaLabel ?? 'select date' };
    }, [ariaLabel, id, label]);

    return (
      <>
        <WbTextField
          ref={ref}
          fullWidth={fullWidth}
          style={style}
          placeholder={placeholder}
          value={value ?? ''}
          error={error}
          helperText={helperText}
          disabled={disabled}
          InputProps={{
            endAdornment: (
              <>
                {value && !disabled && (
                  <ClearButton
                    style={{ visibility: 'visible', pointerEvents: 'initial' }}
                    onClear={() => {
                      onChange(undefined);
                      handleClose();
                    }}
                  />
                )}
                <IconButton
                  aria-label="open calendar"
                  onClick={handleIconClick}
                  size="small"
                  disabled={disabled}
                >
                  <CalendarTodayIcon fontSize="small" />
                </IconButton>
              </>
            ),
          }}
          inputProps={{ readOnly: true, onClick: handleInputClick }}
          InputLabelProps={{
            shrink: true,
          }}
          {...rest}
          {...accessibilityProps}
        />
        <Popover
          disableRestoreFocus
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <div className={classes.modalRoot}>
            <Calendar
              date={selectedDate ?? new Date()}
              onChange={item => handleSelect(item as Date)}
              color={theme.palette.primary.main}
              minDate={minDate}
              maxDate={maxDate}
            />
          </div>
        </Popover>
      </>
    );
  },
);

WbDatePicker.displayName = 'WbDatePicker';
